import {
  ITabletData,
  TabletOperationMode,
  TabletUsageTypeMode
} from "api/types";
import { IAccess } from "components/Gates/ActivationGate";
import { DeviceIdContext } from "components/Gates/DeviceIdCtxProvider";
import gql from "graphql-tag";
import { getIdentity } from "init/tracking";
import * as React from "react";
import { Query } from "react-apollo";
import {
  getBooleanKeyFromLocalStorage,
  getEnumKeyFromLocalStorage,
  getStringKeyFromLocalStorage
} from "utils/localStorage";
import rollBar from "utils/rollbar";
import { TenantDataContext } from "./TenantDataGate";

interface IProps {
  tabletData?: ITabletData;
  children: any;
  access?: IAccess;
}

interface IState {
  loggedTabletData: ITabletData;
}

export const TabletDataContext = React.createContext<ITabletData | undefined>(
  undefined
);

class TabletDataGate extends React.Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = {
      loggedTabletData: this.getTabletDataFromLocalStorage()
    };
  }

  public componentDidMount() {
    const { loggedTabletData } = this.state;
    if (loggedTabletData.tabletId) {
      getIdentity().then(identity =>
        rollBar.info(
          `${identity} - Logged Tablet Data has tablet id ${loggedTabletData.tabletId}`
        )
      );
    }
  }

  public render() {
    const { tabletData, children } = this.props;
    return (
      <TabletDataContext.Provider value={tabletData || undefined}>
        {children}
      </TabletDataContext.Provider>
    );
  }

  public componentDidUpdate() {
    const { tabletData } = this.props;
    if (!tabletData) {
      return;
    }
    const { loggedTabletData } = this.state;
    let logData = false;

    // TODO: abstract tablet data log logic to remove if/else chain
    // TODO: Add tests for this logic
    if (loggedTabletData.tabletId !== tabletData.tabletId) {
      localStorage.setItem("loggedTabletId", tabletData.tabletId);
      loggedTabletData.tabletId = tabletData.tabletId;
      logData = true;
    }
    // operationMode
    if (loggedTabletData.operationMode !== tabletData.operationMode) {
      localStorage.setItem(
        "loggedTabletOperationMode",
        tabletData.operationMode
      );
      loggedTabletData.operationMode = tabletData.operationMode;
      logData = true;
    }
    // whiteLabelMode
    if (loggedTabletData.whiteLabelMode !== tabletData.whiteLabelMode) {
      localStorage.setItem(
        "whiteLabelMode",
        tabletData.whiteLabelMode
          ? tabletData.whiteLabelMode.toString()
          : "false"
      );
      loggedTabletData.whiteLabelMode = tabletData.whiteLabelMode;
      logData = true;
    }
    // showTabletPurchaseFlow
    if (
      loggedTabletData.locationSettings.showTabletPurchaseFlow !==
      tabletData.locationSettings.showTabletPurchaseFlow
    ) {
      localStorage.setItem(
        "showTabletPurchaseFlow",
        tabletData.locationSettings.showTabletPurchaseFlow
          ? tabletData.locationSettings.showTabletPurchaseFlow.toString()
          : "false"
      );
      loggedTabletData.locationSettings.showTabletPurchaseFlow =
        tabletData.locationSettings.showTabletPurchaseFlow;
      logData = true;
    }
    // showTabletRewardsFlow
    if (
      loggedTabletData.locationSettings.showTabletRewardsFlow !==
      tabletData.locationSettings.showTabletRewardsFlow
    ) {
      localStorage.setItem(
        "showTabletRewardsFlow",
        tabletData.locationSettings.showTabletRewardsFlow
          ? tabletData.locationSettings.showTabletRewardsFlow.toString()
          : "false"
      );
      loggedTabletData.locationSettings.showTabletRewardsFlow =
        tabletData.locationSettings.showTabletRewardsFlow;
      logData = true;
    }
    // showTabletPromoCodesFlow
    if (
      loggedTabletData.locationSettings.showTabletPromoCodesFlow !==
      tabletData.locationSettings.showTabletPromoCodesFlow
    ) {
      localStorage.setItem(
        "showTabletPromoCodesFlow",
        tabletData.locationSettings.showTabletPromoCodesFlow
          ? tabletData.locationSettings.showTabletPromoCodesFlow.toString()
          : "false"
      );
      loggedTabletData.locationSettings.showTabletPromoCodesFlow =
        tabletData.locationSettings.showTabletPromoCodesFlow;
      logData = true;
    }

    if (loggedTabletData.tenantId !== tabletData.tenantId) {
      localStorage.setItem(
        "tenantId",
        tabletData.tenantId ? tabletData.tenantId : ""
      );
      loggedTabletData.tenantId = tabletData.tenantId;
      logData = true;
    }

    if (logData) {
      getIdentity().then(identity =>
        rollBar.info(
          `${identity} - Tablet Data Updated with tablet id ${
            loggedTabletData.tabletId
          } in mode ${loggedTabletData.operationMode} ${
            tabletData.whiteLabelMode ? "not" : ""
          } running as white labeled tenant`
        )
      );

      if (this.props.access) {
        this.props.access.logTabletId(loggedTabletData.tabletId);
      }
    }
    // console.log("loggedTabletData", this.state.loggedTabletData);
  }
  private getTabletDataFromLocalStorage(): ITabletData {
    return {
      tabletId: getStringKeyFromLocalStorage("loggedTabletId"),
      usageTypeMode: getEnumKeyFromLocalStorage<TabletUsageTypeMode>(
        "loggedTabletUsageTypeMode",
        TabletUsageTypeMode.Unset
      ),
      operationMode: getEnumKeyFromLocalStorage<TabletOperationMode>(
        "loggedTabletOperationMode",
        TabletOperationMode.PointOfSale
      ),
      whiteLabelMode: getBooleanKeyFromLocalStorage("whiteLabelMode"),
      locationSettings: {
        showTabletPurchaseFlow: getBooleanKeyFromLocalStorage(
          "showTabletPurchaseFlow"
        ),
        showTabletRewardsFlow: getBooleanKeyFromLocalStorage(
          "showTabletRewardsFlow"
        ),
        showTabletPromoCodesFlow: getBooleanKeyFromLocalStorage(
          "showTabletPromoCodesFlow"
        )
      },
      tenantId: getStringKeyFromLocalStorage("tenantId")
    };
  }

  // TODO: Use method to set info once
  // private setRollbarInfo(tabletId: string) {
  //   rollBar.info(`${identity} - Logged Tablet Data has tablet id ${tabletId}`);
  // }
}

export default (props: IProps) => (
  <Query
    query={gql`
      fragment planFields on TenantTabletSelfInfo {
        tablet {
          tabletId
          operationMode
          usageTypeMode
        }
      }
      fragment locationSettings on TenantTabletSelfInfo {
        locationSettings {
          showTabletPromoCodesFlow
          showTabletPurchaseFlow
          showTabletRewardsFlow
        }
      }
      query pollData($deviceFingerprint: String, $codeRevision: String) {
        pingTablet(
          deviceFingerprint: $deviceFingerprint
          codeRevision: $codeRevision
        )
        me {
          ...planFields
          ...locationSettings
        }
      }
    `}
    variables={{
      deviceFingerprint: React.useContext(DeviceIdContext).deviceFingerprint,
      codeRevision: React.useContext(DeviceIdContext).codeRevision
    }}
    fetchPolicy="cache-and-network"
    pollInterval={15000}
  >
    {({ data }) => {
      // TODO: Find way to ensure data is passed down without resorting to if/else
      // Using loading prop from <Query/> triggers a rerender every 1500ms (pollInterval),
      // that might annoy users.
      return (
        <TenantDataContext.Consumer>
          {tenant => {
            return (
              <TabletDataGate
                {...props}
                tabletData={
                  (data &&
                    data.me &&
                    tenant &&
                    tenant.interests && {
                      ...data.me.tablet,
                      locationSettings: data.me.locationSettings,
                      whiteLabelMode: tenant.whiteLabelMode,
                      tenantId: tenant.id,
                      hasNoInterests: tenant.interests.length === 0
                    }) ||
                  undefined
                }
              />
            );
          }}
        </TenantDataContext.Consumer>
      );
    }}
  </Query>
);



// WEBPACK FOOTER //
// ./src/components/Gates/TabletDataGate.tsx